import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import { CompetitionService } from '../../service/CompetitionService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EnumService from '../../service/EnumService';
import CompetitionUtils from '../../utilities/CompetitionUtils';
import SecurityService from '../../service/SecurityService';
import CompetitionJobEditDialog from '../../pages/competition/CompetitionJobEditDialog';
import { Divider } from 'primereact/divider';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InstitutionService } from '../../service/InstitutionService';
import { Checkbox } from 'primereact/checkbox';
import CompetitionStorageFolderFileList from '../file/CompetitionStorageFolderFileList';
import ValidationErrorModalComponent from '../ValidationErrorModalComponent';
import CompetitionChangeReasonDialog from '../../pages/competition/CompetitionChangeReasonDialog';
import useAuth from '../../hooks/useAuth';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import GeneralUtils from '../../utilities/GeneralUtils';
import DOMPurify from 'dompurify';

const CompetitionJobViewComponent = (props) => {
    const [competition, setCompetition] = useState(null);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [competitionStatuses, setCompetitionStatuses] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [rejectReasons, setRejectReasons] = useState([]);
    const [retireReason, setRetireReason] = useState([]);
    const [hasEditPermission, setHasEditPermission] = useState(false);
    const [hasCancelPermission, setHasCancelPermission] = useState(false);
    const [hasFinalizePermission, setHasFinalizePermission] = useState(false);
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [changeReasonDialogVisible, setChangeReasonDialogVisible] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const [cancelReason, setCancelReason] = useState([]);

    const toast = useRef(null);
    const dt = useRef(null);
    const { auth } = useAuth();

    const competitionService = useMemo(() => new CompetitionService(), []);
    const institutionService = useMemo(() => new InstitutionService(), []);

    const { competitionIdParam } = useParams();


    useEffect(() => {
        const getCompetitionStatuses = async () => {
            setCompetitionStatuses(await EnumService.getEnumByName('CompetitionStatus'));
        };

        const checkEditPermission = async () => {
            setHasEditPermission(await SecurityService.check('CAN_EDIT_JOB_COMPETITION', competitionIdParam));
        };

        const checkCancelPermission = async () => {
            setHasCancelPermission(await SecurityService.check('CAN_CANCEL_JOB_COMPETITION', competitionIdParam));
        };

        const checkFinalizePermission = async () => {
            setHasFinalizePermission(await SecurityService.check('CAN_FINALIZE_NO_APPLICANTS_JOB_COMPETITION', competitionIdParam));
        };

        const getInstitutions = async () => {
            await institutionService.getInstitutionsForUserByCriteria({ name: null })
                .then((_institutions) => {
                    // console.log("institutions: ", _institutions);
                    setInstitutions(_institutions);
                })
                .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        const getCompetition = async () => {
            // console.log(props?.competition);
            setCompetition(props?.competition);

            await checkEditPermission()
            await checkCancelPermission()
            await checkFinalizePermission()
            await getInstitutions()
            await getReasons()
        };

        getCompetitionStatuses();
        getCompetition();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [competitionService, competitionIdParam, props?.competition]);

    const getReasons = async () => {
        await competitionService.getCompetitionChangeReason(props?.competition?.id)
            .then((_reasons) => {
                // console.log("_reasons: ", _reasons);
                if (props?.competition?.status === 'RETIRED') {
                    setRetireReason(_reasons.find(r => r.reasonType === 'RETIRE'));
                }
                if (props?.competition?.status === 'CANCELED') {
                    setCancelReason(_reasons.find(r => r.reasonType === 'CANCEL'));
                }
                let _rejectReasons = _reasons.filter(r => r.reasonType === 'REJECT');
                // console.log(_rejectReasons)
                setRejectReasons(_rejectReasons);
            })
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    };

    const setCompetitionAction = (_action) => {
        if (_action === 'RETIRE' || _action === 'REJECT' || _action === 'CANCEL') {
            setSelectedAction(_action);
            setChangeReasonDialogVisible(true);
        } else {
            confirmDialog({
                ...CompetitionUtils.getConfirmMessageForAction(_action),
                accept: () => acceptAction(_action)
            });
        }
    }

    const hasPermission = (permissionName) => {
        if (auth && auth.user && auth.user.permissions) {
            return auth.user.permissions.indexOf(permissionName) > -1;
        } else {
            return false;
        }
    };

    const acceptAction = (_action, _competition = null) => {
        competitionService.saveCompetitionAction(_competition ? _competition : competition, _action).then(
            (response) => {
                // console.log("response: ", response);
                setCompetition(response.competition)
                getReasons();
                if (typeof props.refresh === 'function') {
                    props.refresh(response.competition);
                }
                toast?.current?.show({ severity: 'success', summary: CompetitionUtils.getSuccessMessageForAction(_action), contentClassName: "pcn-toast-success" });
            },
            (error) => {
                // console.log(error)
                if (error?.hasValidationError) {
                    let _errorCodes = error.errorCodes
                    if (error.errorIDs) {
                        _errorCodes.push({ message: "competition.job.checkExternal.notFound.withParam", param: error.errorIDs })
                    }
                    setValidationErrors(_errorCodes)
                    setShowValidationErrorDialog(true)
                } else {
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
                }
            }
        );
    }

    const afterSave = (savedCompetition, isNew, success, error) => {
        // console.log("afterSave: ", savedCompetition, success, error);
        if (success) {
            setCompetition(savedCompetition)
            toast.current.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
        } else {
            toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
        }
    };

    const afterFileUpload = async (savedData) => {
        // console.log("afterFileUpload: ", savedData);
        if (savedData) {
            setCompetition(savedData);
            if (typeof props.refresh === 'function') {
                props.refresh(savedData);
            }
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />


            <div className='w-full grid pcn-data-view-panel'>
                <div className='col-12 pt-0'>
                    <div className='mt-3'>
                        <Button
                            className="pcn-button-slim-with-icon p-button-success mr-2"
                            onClick={() => setCompetitionAction("APPROVE")}
                            visible={CompetitionUtils.canApproveReject(competition) && hasPermission('COMPETITION_JOB_APPROVAL')}
                        >
                            <div className='flex align-items-center'>
                                <FontAwesomeIcon icon='fa-solid fa-check' className="mr-1 " />
                                <span>{translatedMessage('competition.job.approve')}</span>
                            </div>
                        </Button>

                        <Button
                            className="pcn-button-slim-with-icon p-button-danger mr-2"
                            onClick={() => setCompetitionAction("REJECT")}
                            visible={CompetitionUtils.canApproveReject(competition) && hasPermission('COMPETITION_JOB_APPROVAL')}
                        >
                            <div className='flex align-items-center'>
                                <FontAwesomeIcon icon='fa-solid fa-xmark' className="mr-1 " />
                                <span>{translatedMessage('competition.job.reject')}</span>
                            </div>
                        </Button>

                        <Button
                            className="pcn-button-slim-with-icon p-button-success mr-2"
                            onClick={() => setCompetitionAction("VALIDATE")}
                            visible={CompetitionUtils.canValidate(competition) && hasEditPermission}
                        >
                            <div className='flex align-items-center'>
                                <FontAwesomeIcon icon='fa-solid fa-check' className="mr-1 " />
                                <span>{translatedMessage('competition.job.validate')}</span>
                            </div>
                        </Button>

                        <Button
                            className="pcn-button-slim-with-icon p-button mr-2"
                            onClick={() => setCompetitionAction("UNLOCK")}
                            visible={CompetitionUtils.canMoveToDraft(competition) && hasEditPermission}
                        >
                            <div className='flex align-items-center'>
                                <FontAwesomeIcon icon='fa-solid fa-pencil' className="mr-1 " />
                                <span>{translatedMessage('competition.edit')}</span>
                            </div>
                        </Button>

                        <Button
                            className="pcn-button-slim-with-icon p-button-warning mr-2"
                            onClick={() => setCompetitionAction("RETIRE")}
                            visible={CompetitionUtils.canRetire(competition) && hasEditPermission}
                        >
                            <div className='flex align-items-center'>
                                <FontAwesomeIcon icon='fa-solid fa-triangle-exclamation' className="mr-1 " />
                                <span>{translatedMessage('competition.retire')}</span>
                            </div>
                        </Button>

                        <Button
                            className="pcn-button-slim-with-icon p-button-warning mr-2"
                            onClick={() => setCompetitionAction("FINALIZE")}
                            visible={hasFinalizePermission}
                        >
                            <div className='flex align-items-center'>
                                <FontAwesomeIcon icon='fa-solid fa-triangle-exclamation' className="mr-1 " />
                                <span>{translatedMessage('competition.finalize_no_applications')}</span>
                            </div>
                        </Button>

                        <Button
                            className="pcn-button-slim-with-icon p-button-danger mr-2"
                            onClick={() => setCompetitionAction("CANCEL")}
                            visible={hasCancelPermission}
                        >
                            <div className='flex align-items-center'>
                                <FontAwesomeIcon icon='fa-solid fa-triangle-exclamation' className="mr-1 " />
                                <span>{translatedMessage('competition.cancel')}</span>
                            </div>
                        </Button>

                        <Button
                            className="pcn-button-slim-with-icon p-button mr-2"
                            onClick={() => setEditDialogVisible(true)}
                            visible={hasEditPermission}
                            disabled={!CompetitionUtils.canEdit(competition)}
                        >
                            <div className='flex align-items-center'>
                                <FontAwesomeIcon icon='fa-solid fa-pencil' className="mr-1 " />
                                <span>{translatedMessage('competition.details.edit')}</span>
                            </div>
                        </Button>
                    </div>
                    <Divider />
                    <h5 className='mb-1 font-weight-normal'>{translatedMessage("competition.details")}</h5>
                </div>
                <div className='col-12 md:col-6'>
                    <div className='filed-label'>{translatedMessage('competition.institution')}</div>
                    <div className='filed-value'>{competition?.institution?.name ? competition?.institution.name : "-"}</div>
                </div>
                <div className='col-12 md:col-6'>
                    <div className='filed-label'>{translatedMessage('competition.positionCategory')}</div>
                    <div className='filed-value'>{translatedMessage("job.PositionCategory." + competition?.positionCategory)}</div>
                </div>
                <div className='col-12'>
                    <div className='filed-label'>{translatedMessage('competition.legalBasis')}</div>
                    <div className='filed-value'>{competition?.legalBasis ? competition?.legalBasis?.label : "-"}</div>
                </div>
                <div className='col-12 md:col-6'>
                    <div className='filed-label'>{translatedMessage('competition.institutionRegistrationNumber')}</div>
                    <div className='filed-value'>{GeneralUtils.dashIfNullForObject(competition?.institutionRegistrationNumber) + " / " + GeneralUtils.formatDate(competition?.institutionRegistrationDate)}</div>
                </div>
                <div className='col-12 md:col-6'>
                    <div className='filed-label'>{translatedMessage('competition.registrationNumber')}</div>
                    <div className='filed-value'>{GeneralUtils.dashIfNullForObject(competition?.registrationNumber) + " / " + GeneralUtils.formatDate(competition?.registrationDate)}</div>
                </div>
                {competition?.clarificationInterval &&
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.clarificationInterval')}</div>
                        <div className='filed-value'>{competition?.clarificationInterval} {translatedMessage("generic.days")}</div>
                    </div>
                }
                {competition?.contestationInterval &&
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.contestationInterval')}</div>
                        <div className='filed-value'>{competition?.contestationInterval} {translatedMessage("generic.days")}</div>
                    </div>
                }
                {competition?.writtenTestDate && <>
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.writtenTestDate')}</div>
                        <div className='filed-value'>{GeneralUtils.formatDateTime(competition?.writtenTestDate)}</div>
                    </div>
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.interviewTestDate')}</div>
                        <div className='filed-value'>{GeneralUtils.formatDateTime(competition?.interviewTestDate)}</div>
                    </div>

                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.supplementaryTest1Name')}</div>
                        <div className='filed-value'>{competition?.supplementaryTest1Name ? competition?.supplementaryTest1Name : "-"}</div>
                    </div>
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.supplementaryTest1Date')}</div>
                        <div className='filed-value'>{GeneralUtils.formatDateTime(competition?.supplementaryTest1Date)}</div>
                    </div>

                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.supplementaryTest2Name')}</div>
                        <div className='filed-value'>{competition?.supplementaryTest2Name ? competition?.supplementaryTest2Name : "-"}</div>
                    </div>
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.supplementaryTest2Date')}</div>
                        <div className='filed-value'>{GeneralUtils.formatDateTime(competition?.supplementaryTest2Date)}</div>
                    </div>

                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.supplementaryTest3Name')}</div>
                        <div className='filed-value'>{competition?.supplementaryTest3Name ? competition?.supplementaryTest3Name : "-"}</div>
                    </div>
                    <div className='col-12 md:col-6'>
                        <div className='filed-label'>{translatedMessage('competition.supplementaryTest3Date')}</div>
                        <div className='filed-value'>{GeneralUtils.formatDateTime(competition?.supplementaryTest3Date)}</div>
                    </div>
                </>}
                <div className='col-12 mt-2'>
                    <div className="flex align-items-center">
                        <Checkbox checked={competition?.hasResponsibility} disabled />
                        <label className="ml-2">{translatedMessage("competition.hasResponsibility")}</label>
                    </div>
                </div>

                <div className='col-12'>
                    <div className="flex align-items-center">
                        <Checkbox checked={competition?.anfpRequest} disabled />
                        <label className="ml-2">{translatedMessage("competition.anfpRequest")}</label>
                    </div>
                </div>

                <div className='col-12'><Divider /></div>

                {competition?.contactName && <>
                    <div className="col-12"><h5 className='mb-0'>{translatedMessage("competition.contactPerson")}</h5></div>
                    <div className='col-12 md:col-3'>
                        <div className='filed-label'>{translatedMessage('competition.contactName')}</div>
                        <div className='filed-value'>{competition?.contactName ? competition?.contactName : "-"}</div>
                    </div>
                    <div className='col-12 md:col-3'>
                        <div className='filed-label'>{translatedMessage('competition.contactPosition')}</div>
                        <div className='filed-value'>{competition?.contactPosition ? competition?.contactPosition : "-"}</div>
                    </div>
                    <div className='col-12 md:col-3'>
                        <div className='filed-label'>{translatedMessage('competition.contactPhone')}</div>
                        <div className='filed-value'>{competition?.contactPhone ? competition?.contactPhone : "-"}</div>
                    </div>
                    <div className='col-12 md:col-3'>
                        <div className='filed-label'>{translatedMessage('competition.contactEmail')}</div>
                        <div className='filed-value'>{competition?.contactEmail ? competition?.contactEmail : "-"}</div>
                    </div>
                </>}

                {competition?.responsibleName && <>
                    <div className="col-12 mt-3"><h5 className='mb-0'>{translatedMessage("competition.responsiblePerson")}</h5></div>
                    <div className='col-12 md:col-3'>
                        <div className='filed-label'>{translatedMessage('competition.responsibleName')}</div>
                        <div className='filed-value'>{competition?.responsibleName ? competition?.responsibleName : "-"}</div>
                    </div>
                    <div className='col-12 md:col-3'>
                        <div className='filed-label'>{translatedMessage('competition.responsiblePosition')}</div>
                        <div className='filed-value'>{competition?.responsiblePosition ? competition?.responsiblePosition : "-"}</div>
                    </div>
                    <div className='col-12 md:col-3'>
                        <div className='filed-label'>{translatedMessage('competition.responsiblePhone')}</div>
                        <div className='filed-value'>{competition?.responsiblePhone ? competition?.responsiblePhone : "-"}</div>
                    </div>
                    <div className='col-12 md:col-3'>
                        <div className='filed-label'>{translatedMessage('competition.responsibleEmail')}</div>
                        <div className='filed-value'>{competition?.responsibleEmail ? competition?.responsibleEmail : "-"}</div>
                    </div>
                </>}

                {competition?.managerName && <>
                    <div className="col-12 mt-3"><h5 className='mb-0'>{translatedMessage("competition.institution.manager.info")}</h5></div>
                    <div className='col-12 md:col-6 lg:col-3'>
                        <div className='filed-label'>{translatedMessage('competition.managerName')}</div>
                        <div className='filed-value'>{competition?.managerName ? competition?.managerName : "-"}</div>
                    </div>
                    <div className='col-12 md:col-6 lg:col-3'>
                        <div className='filed-label'>{translatedMessage('competition.managerPosition')}</div>
                        <div className='filed-value'>{competition?.managerPosition ? competition?.managerPosition : "-"}</div>
                    </div>
                </>}

                {competition?.status === 'CANCELED' && cancelReason?.reason && <>
                    <div className='col-12'><Divider /></div>
                    <div className='col-12'>
                        <h5 className='mb-1'>{translatedMessage('competition.cancelReason')}</h5>
                        <div className='filed-value'>{cancelReason.reason}</div>
                    </div>
                </>}

                {competition?.status === 'RETIRED' && retireReason?.reason && <>
                    <div className='col-12'><Divider /></div>
                    <div className='col-12'>
                        <h5 className='mb-1'>{translatedMessage('competition.retireReason')}</h5>
                        <div className='filed-value'>{retireReason.reason}</div>
                    </div>
                </>}

                {rejectReasons.length > 0 && <>
                    <div className='col-12'><Divider /></div>
                    <div className='col-12'>
                        <h5 className='mb-1'>{translatedMessage('competition.rejectReasons')}</h5>
                        <DataTable
                            ref={dt}
                            value={rejectReasons}
                            dataKey="id"
                            paginator={props?.hidePaginator ? false : true}
                            rows={5}
                            className="datatable-responsive pcn-datatable"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            currentPageReportTemplate={"{first} - {last} " + translatedMessage('generic.of') + " {totalRecords}"}
                            emptyMessage={translatedMessage("generic.tableEmptyMessage")}
                            responsiveLayout="stack"
                            sortField="lastModifiedOn"
                        >
                            <Column
                                field="reason"
                                header={translatedMessage("competition.changeReason")}
                                sortable
                                headerStyle={{ width: '80%', minWidth: '8rem' }}
                            />
                            <Column
                                field="lastModifiedOn"
                                header={translatedMessage('generic.lastUpdated.on')}
                                sortable
                                body={(e) => GeneralUtils.formatDate(e.lastModifiedOn)}
                                headerStyle={{ width: '10%', minWidth: '8rem' }}
                            />
                        </DataTable>
                    </div>
                </>}

                {competition?.folder &&
                    <div className='col-12 mt-5'>
                        {rejectReasons.length === 0 ? <Divider /> : <></>}

                        <CompetitionStorageFolderFileList
                            competition={competition}
                            showUploadButton={false}
                            hasDelete={true}
                            pageTitle={translatedMessage('generic.files')}
                            afterFileUpload={afterFileUpload}
                        />
                    </div>
                }

                {competition?.description &&
                    <div className='col-12'>
                        <div className='filed-label'>{translatedMessage('generic.description')}</div>
                        <div className='filed-value'>
                            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(competition.description) }} />
                        </div>
                    </div>
                }
                {competition?.bibliography &&
                    <div className='col-12'>
                        <div className='filed-label'>{translatedMessage('competition.bibliography')}</div>
                        <div className='filed-value'>
                            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(competition.bibliography) }} />
                        </div>
                    </div>
                }
            </div>

            {editDialogVisible &&
                <CompetitionJobEditDialog
                    value={competition}
                    visible={editDialogVisible}
                    afterSave={afterSave}
                    visibleSetter={() => setEditDialogVisible(false)}
                    statuses={competitionStatuses}
                    institutions={institutions}
                />
            }

            {changeReasonDialogVisible &&
                <CompetitionChangeReasonDialog
                    value={competition}
                    action={selectedAction}
                    visible={changeReasonDialogVisible}
                    afterSave={acceptAction}
                    visibleSetter={() => setChangeReasonDialogVisible(false)}
                />
            }

            {showValidationErrorDialog &&
                <ValidationErrorModalComponent
                    visible={showValidationErrorDialog}
                    errors={validationErrors}
                    closeDialog={() => setShowValidationErrorDialog(false)}
                />
            }
        </>
    );
};

export default CompetitionJobViewComponent;
